<template>
  <div
    ref="selectRef"
    class="SMSelect"
    :class="{ SMSelect_disabled: disabled }"
    role="combobox"
    aria-haspopup="listbox"
    :aria-expanded="isOpen"
    tabindex="0"
    @click="disabled ? undefined : (isOpen = !isOpen)"
    @keydown.enter.space.stop="disabled ? undefined : toggleDropdown()"
    @keydown.esc.stop="isOpen = false"
    @keydown.arrow-down.stop="disabled ? undefined : navigateOptions(1)"
    @keydown.arrow-up.stop="disabled ? undefined : navigateOptions(-1)"
    @blur="scheduleForClose"
  >
    <div class="flex justify-between items-center text-sm grow">
      <div class="grow" :class="{ 'text-gray-300': !selectedLabel }">{{ selectedLabel ?? placeholder }}</div>
      <SMIcon
        v-if="clearable && selectedLabel"
        icon="remove_circle_outline"
        size="1em"
        class="text-gray-400"
        title="Clear Selection"
        @click.stop="selectOption(null)"
      />
      <SMIcon icon="keyboard_arrow_down" />
    </div>
    <transition>
      <Teleport to="body">
        <div v-show="isOpen" :style="selectPosition" class="absolute pointer-events-none" @click.stop>
          <div
            ref="dropdownRef"
            class="absolute mt-1 bg-white border rounded shadow z-10 overflow-auto pointer-events-auto min-w-full"
            :class="positionClasses"
            :style="{ 'max-height': maxHeight + 'px' }"
            @wheel.stop
          >
            <slot>
              <SMOption v-for="(label, key) in options" :key="key" :value="key" :label="label" />
            </slot>
          </div>
        </div>
      </Teleport>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, provide, onMounted, toRef, PropType } from 'vue';
import SMOption from './SMOption.vue';
import { InjectOptions, PMSelectInjectionKey } from './SMSelect.types';
import SMIcon from './SMIcon.vue';
import { watch } from 'vue';
import { nextTick } from 'vue';
import { onBeforeUnmount } from 'vue';

const props = defineProps({
  options: {
    type: Object,
  },
  modelValue: {
    type: null,
  },
  disabled: {
    type: Boolean,
  },
  maxHeight: {
    type: Number,
    default: 300,
  },
  placeholder: {
    type: String,
    default: 'Please Select...',
  },
  clearable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ (event: 'update:modelValue', value: any): void }>();

const isOpen = ref(false);
const optionsMap = ref(new Map<string | number | boolean | undefined | null, InjectOptions>());

const addOption = (value: string | number | boolean | null | undefined, options: InjectOptions) => {
  optionsMap.value.set(value, options);
};

let timeout: ReturnType<typeof setTimeout> | undefined;
const scheduleForClose = () => {
  timeout = setTimeout(() => {
    isOpen.value = false;
  }, 100);
};

const selectOption = (value: string | number | boolean | undefined | null) => {
  emit('update:modelValue', value);
  isOpen.value = false;
};

const removeOption = (value: string | number | boolean | undefined | null) => {
  optionsMap.value.delete(value);
};

const stopCloseTimer = () => {
  setTimeout(() => {
    clearTimeout(timeout);
  }, 10);
};

provide(PMSelectInjectionKey, {
  addOption,
  removeOption,
  selectOption,
  selectedValue: toRef(props, 'modelValue'),
  stopCloseTimer,
});

// onMounted(() => {
//   console.log('laksjdlkajsldlj');
//   // Sort the optionsMap according to their DOM order
//   const sortedEntries = [...optionsMap.entries()].sort((a, b) => {
//     if (!a[1].ref || !b[1].ref) return 0;
//     const position = a[1].ref.compareDocumentPosition(b[1].ref);
//     if (position & Node.DOCUMENT_POSITION_FOLLOWING || position & Node.DOCUMENT_POSITION_CONTAINED_BY) {
//       return -1;
//     } else {
//       return 1;
//     }
//   });
//   optionsMap.clear();
//   for (const [ref, selectMe] of sortedEntries) {
//     optionsMap.set(ref, selectMe);
//   }
// });

const selectedLabel = computed(() => {
  const config = optionsMap.value.get(props.modelValue);
  return config?.label;
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const navigateOptions = (direction: number) => {
  const keys = [...optionsMap.value.keys()];
  const currentIndex = keys.indexOf(props.modelValue);
  const newIndex = currentIndex + direction;

  if (newIndex >= 0 && newIndex < keys.length) {
    const newValue = keys[newIndex];
    emit('update:modelValue', newValue);
    nextTick(() => {
      const option = optionsMap.value.get(newValue);
      option?.ref?.scrollIntoView({ block: 'nearest' });
    });
  }
};

const selectRef = ref<HTMLElement>();
const dropdownRef = ref<HTMLElement>();

const positionClasses = ref('top-full left-0');

watch(isOpen, (value) => {
  if (value) {
    nextTick(() => {
      const dropdownRect = dropdownRef.value?.getBoundingClientRect();

      if (!dropdownRect) return;

      const { width, height, left, top } = dropdownRect;

      let classNames = '';

      if (left + width > window.innerWidth) {
        classNames += ' right-0';
      } else {
        classNames += ' left-0';
      }

      if (top + height > window.innerHeight) {
        classNames += ' bottom-full';
      } else {
        classNames += ' top-full';
      }

      positionClasses.value = classNames;
    });
  } else {
    positionClasses.value = 'top-full left-0';
  }
});

const selectPosition = ref({
  top: '0px',
  left: '0px',
  width: '0px',
  height: '0px',
});

const recordSelectPosition = () => {
  if (!selectRef.value) return;
  const { top, left, width, height } = selectRef.value.getBoundingClientRect();
  // add scroll position to top and left
  selectPosition.value = {
    top: `${top + window.scrollY}px`,
    left: `${left + window.scrollX}px`,
    width: `${width}px`,
    height: `${height}px`,
  };
};

const onOutsideClick = (event: MouseEvent) => {
  if (!isOpen.value) return;
  if (selectRef.value?.contains(event.target as Node)) return;
  if (dropdownRef.value?.contains(event.target as Node)) return;
  console.log('outside click');
  isOpen.value = false;
};

onMounted(() => {
  recordSelectPosition();

  document.addEventListener('click', onOutsideClick);
  window.addEventListener('resize', recordSelectPosition);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onOutsideClick);
  window.removeEventListener('resize', recordSelectPosition);
});
</script>

<style>
.SMSelect {
  @apply w-full flex bg-white rounded border px-2 py-1;
  @apply relative focus:outline-none focus:ring-1 focus:ring-blue-500;
  @apply hover:bg-gray-50 hover:shadow-sm transition-shadow duration-300;
}

.SMSelect_disabled {
  @apply bg-gray-100 text-gray-500 pointer-events-none;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
